/**
 * Estados de dado como TIPO, nao como convencao de renderizacao.
 *
 * Back Office SPEC §3 e KPI Dictionary §1 exigem que ausente, obsoleto,
 * estimado e indisponivel sejam VISUALMENTE distintos, e que ausente NUNCA
 * signifique zero. Wireframes §13: nenhum estado vazio pode mostrar um zero
 * enganoso.
 *
 * A forma de garantir isso e nao ter como renderizar um numero sem declarar o
 * estado dele. O tipo abaixo obriga quem chama a escolher.
 */

export type DataState =
  /** Valor observado, dentro do SLA de frescor. */
  | { kind: 'available'; value: number; unit: string; dataThrough: string | null }
  /** Valor ESTIMADO: exige metodo e confianca (AT-152). */
  | {
      kind: 'estimated';
      value: number;
      unit: string;
      method: string;
      confidence: number;
      dataThrough: string | null;
    }
  /** O provedor nao forneceu o dado (AT-149). Nunca zero. */
  | { kind: 'unavailable'; reason: string }
  /** Falta policy aprovada pelo dono (AT-151). Nunca um score inventado. */
  | { kind: 'not_configured'; missing: string }
  /** Fora do SLA de frescor (AT-150). Nunca mostrado como saudavel. */
  | { kind: 'stale'; value: number | null; unit: string; dataThrough: string | null }
  /** Amostra insuficiente, inclusive denominador zero (AT-155). */
  | { kind: 'insufficient_sample'; reason: string }
  /**
   * Cobertura de CLASSIFICAÇÃO insuficiente (decisão do dono, 2026-09-10).
   *
   * Estado próprio, e não `insufficient_sample`: um fala de TAMANHO da
   * amostra e se resolve esperando mais audiência; este fala de COBERTURA e
   * se resolve classificando a audiência que já existe. Mostrar a mensagem
   * errada manda quem lê tentar a coisa errada.
   */
  | {
      kind: 'not_enough_classified_audience';
      coverage: number;
      minimumCoverage: number | null;
      minimumConfidence: number | null;
    }
  /** Ainda nao implementado nesta fase. Honesto e explicito. */
  | { kind: 'phase_pending'; phase: string };

/** Rotulo em portugues, com o identificador estavel disponivel no detalhe. */
export const DATA_STATE_LABEL: Record<DataState['kind'], string> = {
  available: 'disponível',
  estimated: 'estimado',
  unavailable: 'indisponível',
  not_configured: 'não configurado',
  stale: 'desatualizado',
  insufficient_sample: 'amostra insuficiente',
  not_enough_classified_audience: 'audiência não classificada o suficiente',
  phase_pending: 'fase pendente',
};

/**
 * Marcador textual do estado.
 *
 * O significado NAO depende só de cor (Back Office §20, WCAG 2.2 AA): cada
 * estado tem um sinal textual proprio, legivel por leitor de tela e visivel em
 * monocromatico.
 */
export const DATA_STATE_MARK: Record<DataState['kind'], string> = {
  available: '',
  estimated: '≈',
  unavailable: '∅',
  not_configured: '?',
  stale: '!',
  insufficient_sample: 'n<',
  // Marcador PRÓPRIO, e não o mesmo de amostra insuficiente: em monocromático
  // os dois estados precisam ser distinguíveis, e a ação para resolver cada um
  // é diferente.
  not_enough_classified_audience: '%<',
  phase_pending: '·',
};

/**
 * Como o valor aparece na tela.
 * Estado sem valor devolve o ROTULO, jamais `0` nem string vazia.
 */
export function renderDataState(state: DataState): string {
  switch (state.kind) {
    case 'available':
      return `${formatNumber(state.value)} ${state.unit}`.trim();
    case 'estimated':
      return `${DATA_STATE_MARK.estimated} ${formatNumber(state.value)} ${state.unit}`.trim();
    case 'stale':
      return state.value === null
        ? DATA_STATE_LABEL.stale
        : `${formatNumber(state.value)} ${state.unit} (${DATA_STATE_LABEL.stale})`;
    case 'unavailable':
      return DATA_STATE_LABEL.unavailable;
    case 'not_configured':
      return DATA_STATE_LABEL.not_configured;
    case 'insufficient_sample':
      return DATA_STATE_LABEL.insufficient_sample;
    case 'not_enough_classified_audience':
      // O RÓTULO, e não a cobertura.
      //
      // A primeira versão devolvia `40% classificada`, e um guard do próprio
      // repo pegou: nenhum north-star pode exibir número quando o estado não é
      // `available`. E ele está certo por um motivo que não é formal: quem bate
      // o olho leria "40%" como se fosse a nota de qualidade da audiência.
      //
      // A cobertura continua visível, no lugar certo: na explicação do estado e
      // no detalhe do indicador, onde ela é lida como cobertura.
      return DATA_STATE_LABEL.not_enough_classified_audience;
    case 'phase_pending':
      return DATA_STATE_LABEL.phase_pending;
  }
}

/** `true` quando a UI PODE tratar o valor como numero confiavel. */
export function hasTrustworthyValue(state: DataState): boolean {
  return state.kind === 'available' || state.kind === 'estimated';
}

/**
 * Numero escrito a mao, como o dinheiro (playbook: `Intl` nao e deterministico
 * entre ambientes e insere espaco nao-quebravel).
 */
export function formatNumber(value: number): string {
  const negative = value < 0;
  const abs = Math.abs(value);
  const isInteger = Number.isInteger(abs);
  const units = Math.trunc(abs);
  const decimals = isInteger ? '' : `,${String(Math.round((abs - units) * 100)).padStart(2, '0')}`;

  const digits = String(units);
  let grouped = '';
  for (let i = 0; i < digits.length; i += 1) {
    const fromEnd = digits.length - i;
    grouped += digits[i];
    if (fromEnd > 1 && (fromEnd - 1) % 3 === 0) grouped += '.';
  }
  return `${negative ? '-' : ''}${grouped}${decimals}`;
}

/** Traduz o status de KPI vindo da API para o estado da UI. */
export function dataStateFromKpi(kpi: {
  status: string;
  value: number | null;
  unit: string;
  data_through?: string | null;
  confidence?: number | null;
  /**
   * Cobertura de classificação, quando o indicador tem uma.
   *
   * Vem junto TAMBÉM nos estados sem nota, de propósito: é a cobertura que
   * explica por que a nota não aparece.
   */
  coverage?: {
    classified_coverage: number;
    minimum_classified_coverage: number | null;
    minimum_profile_confidence: number | null;
  } | null;
}): DataState {
  switch (kpi.status) {
    case 'available':
      return {
        kind: 'available',
        value: kpi.value ?? 0,
        unit: kpi.unit,
        dataThrough: kpi.data_through ?? null,
      };
    case 'estimated':
      return {
        kind: 'estimated',
        value: kpi.value ?? 0,
        unit: kpi.unit,
        method: 'declarado pela API',
        confidence: kpi.confidence ?? 0,
        dataThrough: kpi.data_through ?? null,
      };
    case 'stale':
      return {
        kind: 'stale',
        value: kpi.value,
        unit: kpi.unit,
        dataThrough: kpi.data_through ?? null,
      };
    case 'insufficient_sample':
      return { kind: 'insufficient_sample', reason: 'amostra abaixo da política mínima' };
    case 'not_enough_classified_audience':
      return {
        kind: 'not_enough_classified_audience',
        coverage: kpi.coverage?.classified_coverage ?? 0,
        minimumCoverage: kpi.coverage?.minimum_classified_coverage ?? null,
        minimumConfidence: kpi.coverage?.minimum_profile_confidence ?? null,
      };
    case 'not_configured':
      return { kind: 'not_configured', missing: 'política aprovada pelo dono' };
    default:
      return { kind: 'unavailable', reason: 'fonte do provedor não conectada' };
  }
}
